import { Alert, Col, FormSection, Row, TextInput } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ICustomControlProcedureAddEditProps } from '@frontend/components/pages/customControls/interfaces/';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { FormLabel, HorizontalFormGroup, StackedInputs } from '@frontend/components/elements/forms';
import { TextInputFormGroupField } from '@frontend/components/fields/TextInputField';
import {
    OorkPermissionsAddEdit,
    OorkV1ProcedureAnamnesis,
    OorkV1ProcedureExamination,
    OorkV1ProcedureSelectRequester,
} from '@frontend/components/pages/customControls/OORK/';
import { OphthalmologyDropzoneProcedureAddEdit } from '@frontend/components/pages/customControls/TFO/';
import { NewOrEditProcedureProcessContainer } from '@frontend/components/pages/customControls/utils/';
import { translateKey } from '@frontend/translate';
import { getValidationErrorTranslation, useEpisodeContext } from '@frontend/utils/index';

export function OorkV1ProcedureAddEdit(props: ICustomControlProcedureAddEditProps): JSX.Element {
    const { patient } = useEpisodeContext();
    const {
        currentProcedure,
        errors,
        handleChangeCustomControl,
        isFirstItem,
        itemState,
        procedure,
        process,
        service,
        validateOneCustomControl,
    } = props;

    const [showBsnBetrokkene, setShowBsnBetrokkene] = useState(false);
    const [doBsnBetrokkeneInitialValidation, setDoBsnBetrokkeneInitialValidation] = useState(false);

    const [updatedPatientEmail, setUpdatedPatientEmail] = useState(
        currentProcedure?.customControlData.email_betrokkene
    );

    useEffect(() => {
        loadBsnBetrokkene();
    }, [patient.identifierSocialSecurity]);

    useEffect(() => {
        if (doBsnBetrokkeneInitialValidation) {
            validateOneCustomControl('bsn_betrokkene');
            setDoBsnBetrokkeneInitialValidation(false);
        }
    }, [doBsnBetrokkeneInitialValidation]);

    useEffect(() => {
        if (procedure.customControlData.bsn_betrokkene && errors.has('customControlData.bsn_betrokkene')) {
            setShowBsnBetrokkene(true);
        }
    }, [errors]);

    useEffect(() => {
        if (!procedure.id) {
            setUpdatedPatientEmail(patient.emailAddress);
            handleChangeCustomControl({ email_betrokkene: patient.emailAddress });
        }
    }, []);

    useEffect(() => {
        if (!procedure.id) {
            setUpdatedPatientEmail(patient.mobileNumber);
            handleChangeCustomControl({ mobileNumber: patient.mobileNumber });
        }
    }, []);

    useEffect(() => {
        if (!procedure.id) {
            setUpdatedPatientEmail(patient.homePhoneNumber);
            handleChangeCustomControl({ homePhoneNumber: patient.homePhoneNumber });
        }
    }, []);

    return (
        <NewOrEditProcedureProcessContainer
            currentProcedure={currentProcedure}
            isBioGrader={true}
            isFirstItem={isFirstItem}
            itemState={itemState}
            process={process}
            service={service}
        >
            <FormContextProvider
                value={{
                    placeholderGroupName: 'procedure',
                    item: procedure.customControlData,
                    errors: errors,
                    customControlData: true,
                    onChange: async (key, value) => await handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await validateOneCustomControl(key),
                }}
            >
                <FormSection>
                    {updatedPatientEmail && (
                        <Alert appearance="info">Controleer of het e-mailadres van de betrokkene klopt</Alert>
                    )}
                    <OorkV1ProcedureSelectRequester {...props} />
                    <TextInputFormGroupField
                        error={errors.get('customControlData.zorgdomein_code')}
                        fieldKey="zorgdomein_code"
                        isRequired={true}
                        label="ZorgDomein code"
                        placeholder="Vul de zorgdomein code in..."
                        value={procedure.customControlData.zorgdomein_code}
                        variant="outline"
                    />

                    <TextInputFormGroupField
                        error={errors.get('customControlData.email_betrokkene')}
                        fieldKey="email_betrokkene"
                        isRequired={true}
                        label="E-mailadres betrokkene"
                        placeholder="E-mailadres voor het uitnodigen videoconsult..."
                        variant="outline"
                    />
                    <>
                        <HorizontalFormGroup
                            hasErrors={
                                props.errors.has('customControlData.mobileNumber') ||
                                props.errors.has('customControlData.homePhoneNumber')
                            }
                        >
                            <FormLabel isRequired>{translateKey('common.phoneNumber')}</FormLabel>
                            <StackedInputs>
                                <Row
                                    columnGap={[2, 4]}
                                    rowGap={[2, 4]}
                                >
                                    <Col span={[12, 6]}>
                                        <TextInput
                                            error={getValidationErrorTranslation(
                                                getTelephoneNumberError('customControlData.mobileNumber')
                                            )}
                                            onChange={async (e) => {
                                                await handleChangeCustomControl({
                                                    mobileNumber: e.target.value ? e.target.value : null,
                                                });
                                                await validateOneCustomControl('mobileNumber');
                                                await validateOneCustomControl('homePhoneNumber');
                                            }}
                                            placeholder={translateKey('common.mobileNumber')}
                                            value={procedure.customControlData.mobileNumber ?? ''}
                                            variant="outline"
                                        />
                                    </Col>
                                    <Col span={[12, 6]}>
                                        <TextInput
                                            error={getValidationErrorTranslation(
                                                getTelephoneNumberError('customControlData.homePhoneNumber')
                                            )}
                                            onChange={async (e) => {
                                                await handleChangeCustomControl({
                                                    homePhoneNumber: e.target.value ? e.target.value : null,
                                                });
                                                await validateOneCustomControl('homePhoneNumber');
                                                await validateOneCustomControl('mobileNumber');
                                            }}
                                            placeholder={translateKey('patient.homePhoneNumber')}
                                            value={procedure.customControlData.homePhoneNumber ?? ''}
                                            variant="outline"
                                        />
                                    </Col>
                                </Row>
                            </StackedInputs>
                        </HorizontalFormGroup>
                    </>

                    {(!patient.identifierSocialSecurity || showBsnBetrokkene) && (
                        <TextInputFormGroupField
                            dontValidateOnChange={true}
                            error={getErrorForBsnBetrokkene(errors.get('customControlData.bsn_betrokkene'))}
                            fieldKey="bsn_betrokkene"
                            isRequired={true}
                            label="BSN betrokkene"
                            onBlur={async () => await validateOneCustomControl('bsn_betrokkene')}
                            placeholder="Vul het BSN in..."
                            variant="outline"
                        />
                    )}
                </FormSection>
                <OorkPermissionsAddEdit errors={errors} />
                <OorkV1ProcedureAnamnesis
                    customControlData={procedure.customControlData}
                    errors={errors}
                    handleChangeCustomControl={handleChangeCustomControl}
                    validateOneCustomControl={validateOneCustomControl}
                />
                <OorkV1ProcedureExamination
                    customControlData={procedure.customControlData}
                    errors={errors}
                    handleChangeCustomControl={handleChangeCustomControl}
                    validateOneCustomControl={validateOneCustomControl}
                />
                <FormSection title="Foto's (fundus, OCT, spleetlampfoto, optioneel binoculair gezichtsveldonderzoek)">
                    <OphthalmologyDropzoneProcedureAddEdit
                        assets={procedure.customControlData.files}
                        handleChangeCustomControl={handleChangeCustomControl}
                        isAutoSaveActive={props.isAutoSaveActive}
                        maxFiles={8}
                        onUploading={async () => undefined}
                        procedure={procedure}
                        service={service}
                    />
                </FormSection>
            </FormContextProvider>
        </NewOrEditProcedureProcessContainer>
    );

    async function loadBsnBetrokkene() {
        const bsn_betrokkene = patient.identifierSocialSecurity ?? procedure.customControlData.bsn_betrokkene;
        await handleChangeCustomControl({
            bsn_betrokkene,
        });
        if (bsn_betrokkene) {
            setDoBsnBetrokkeneInitialValidation(true);
        }
    }

    function getErrorForBsnBetrokkene(error?: string) {
        if (error === 'string_base') {
            return 'bsnIsNeededToContinueOork';
        }

        return error;
    }

    function getTelephoneNumberError(fieldKey: string) {
        const error = props.errors.get(fieldKey);
        if (error === 'string_base') {
            return 'onePhoneNumberRequired';
        } else if (error === 'string_pattern.base') {
            return 'invalidPhoneNumber';
        }

        return error;
    }
}
